/**
 * Expandable Dashboard Cards
 * ==========================
 * Makes dashboard section cards expandable with detailed slide-out panels
 * showing data sources, AI insights, and actionable details.
 */

import React, { useState } from 'react';
import Portal from '@/components/common/Portal';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  X, ChevronRight, TrendingUp, TrendingDown, ExternalLink,
  Users, Star, MessageSquare, BarChart3, Package, Cloud,
  Target, Lightbulb, Activity, Database, Brain, CheckCircle2,
  AlertTriangle, Calendar, Clock, DollarSign, ThumbsUp, ThumbsDown,
  Sun, CloudRain, Thermometer, MapPin
} from 'lucide-react';

// Panel configuration for each card type
const PANEL_CONFIG = {
  'staff-performance': {
    title: 'Staff Performance',
    subtitle: 'Team productivity and engagement insights',
    icon: Users,
    color: 'blue',
    navigateTo: '/app/staff-performance',
    sections: [
      {
        title: 'Data Sources',
        icon: Database,
        items: [
          { name: 'POS System', desc: 'Sales per staff member, transaction speed', status: 'connected' },
          { name: 'Deputy Rostering', desc: 'Shift attendance, punctuality, hours worked', status: 'connected' },
          { name: 'Customer Reviews', desc: 'Staff mentions in feedback', status: 'connected' },
          { name: 'Training Records', desc: 'Certifications and skill development', status: 'connected' }
        ]
      },
      {
        title: 'AI Insights',
        icon: Brain,
        insights: [
          { type: 'positive', message: 'Sarah M. sales up 18% this week - consider for shift leader' },
          { type: 'warning', message: '2 staff showing signs of burnout - 35% overtime increase' },
          { type: 'opportunity', message: 'Cross-training kitchen staff could reduce overtime by 12%' }
        ]
      },
      {
        title: 'Actions Available',
        icon: Target,
        actions: [
          { label: 'View Full Leaderboard', path: '/app/staff-performance' },
          { label: 'Schedule Performance Review', path: '/app/staff-performance?action=review' },
          { label: 'Adjust Roster', path: '/app/roster' },
          { label: 'View Training Gaps', path: '/app/staff-performance?tab=training' }
        ]
      }
    ]
  },
  'customer-sentiment': {
    title: 'Customer Sentiment',
    subtitle: 'Review analysis and feedback trends',
    icon: MessageSquare,
    color: 'emerald',
    navigateTo: '/app/customer-insights',
    sections: [
      {
        title: 'Data Sources',
        icon: Database,
        items: [
          { name: 'Google Reviews', desc: 'Public reviews and ratings', status: 'connected' },
          { name: 'Facebook Reviews', desc: 'Social media feedback', status: 'connected' },
          { name: 'TripAdvisor', desc: 'Tourism and travel reviews', status: 'connected' },
          { name: 'Direct Feedback', desc: 'In-house surveys and comments', status: 'connected' }
        ]
      },
      {
        title: 'AI Insights',
        icon: Brain,
        insights: [
          { type: 'positive', message: 'Food quality mentions up 24% - new menu well received' },
          { type: 'warning', message: 'Service speed mentioned negatively in 34% of recent reviews' },
          { type: 'opportunity', message: 'Responding within 24hrs increases return visits by 18%' }
        ]
      },
      {
        title: 'Actions Available',
        icon: Target,
        actions: [
          { label: 'View All Reviews', path: '/app/customer-insights' },
          { label: 'Respond to Reviews', path: '/app/customer-insights?action=respond' },
          { label: 'View Sentiment Trends', path: '/app/customer-insights?tab=trends' },
          { label: 'Generate Response Templates', path: '/app/customer-insights?action=templates' }
        ]
      }
    ]
  },
  'competitor-intelligence': {
    title: 'Competitor Intelligence',
    subtitle: 'Market positioning and competitive analysis',
    icon: BarChart3,
    color: 'violet',
    navigateTo: '/app/market-intelligence',
    sections: [
      {
        title: 'Data Sources',
        icon: Database,
        items: [
          { name: 'Google Maps', desc: 'Competitor locations, ratings, reviews', status: 'connected' },
          { name: 'Social Media', desc: 'Competitor activity and engagement', status: 'connected' },
          { name: 'Industry Reports', desc: 'ABS, IBISWorld market data', status: 'connected' },
          { name: 'Menu Scraping', desc: 'Competitor pricing (where available)', status: 'partial' }
        ]
      },
      {
        title: 'AI Insights',
        icon: Brain,
        insights: [
          { type: 'warning', message: 'Competitor launched happy hour - monitor traffic impact' },
          { type: 'positive', message: 'Your Google rating (4.6) is highest in 2km radius' },
          { type: 'opportunity', message: 'Gap in market for weekend brunch - competitors close early' }
        ]
      },
      {
        title: 'Actions Available',
        icon: Target,
        actions: [
          { label: 'View Competitor Map', path: '/app/market-intelligence' },
          { label: 'Compare Pricing', path: '/app/market-intelligence?tab=pricing' },
          { label: 'Track Competitor Reviews', path: '/app/market-intelligence?tab=reviews' },
          { label: 'Market Gap Analysis', path: '/app/market-intelligence?tab=gaps' }
        ]
      }
    ]
  },
  'supplier-performance': {
    title: 'Supplier Performance',
    subtitle: 'Vendor reliability and cost tracking',
    icon: Package,
    color: 'amber',
    navigateTo: '/app/suppliers',
    sections: [
      {
        title: 'Data Sources',
        icon: Database,
        items: [
          { name: 'Invoice System', desc: 'Pricing, payment history, credit terms', status: 'connected' },
          { name: 'Delivery Records', desc: 'On-time delivery, order accuracy', status: 'connected' },
          { name: 'Quality Logs', desc: 'Product quality incidents', status: 'manual' },
          { name: 'Market Pricing', desc: 'Industry price benchmarks', status: 'connected' }
        ]
      },
      {
        title: 'AI Insights',
        icon: Brain,
        insights: [
          { type: 'positive', message: 'Seafood supplier 98% on-time - best in category' },
          { type: 'warning', message: 'Produce costs up 8% vs market average' },
          { type: 'opportunity', message: 'Consolidating orders could save $340/month in delivery fees' }
        ]
      },
      {
        title: 'Actions Available',
        icon: Target,
        actions: [
          { label: 'View All Suppliers', path: '/app/suppliers' },
          { label: 'Compare Supplier Prices', path: '/app/suppliers?tab=pricing' },
          { label: 'Review Contracts', path: '/app/suppliers?tab=contracts' },
          { label: 'Log Quality Issue', path: '/app/suppliers?action=log-issue' }
        ]
      }
    ]
  },
  'weather-impact': {
    title: 'Weather Impact',
    subtitle: 'Weather-based demand forecasting',
    icon: Cloud,
    color: 'cyan',
    navigateTo: '/app/forecasting',
    sections: [
      {
        title: 'Data Sources',
        icon: Database,
        items: [
          { name: 'Bureau of Meteorology', desc: '7-day forecast, historical weather', status: 'connected' },
          { name: 'POS History', desc: 'Sales correlated with weather patterns', status: 'connected' },
          { name: 'Event Calendar', desc: 'Local events affecting foot traffic', status: 'connected' },
          { name: 'School Calendar', desc: 'Holidays and term dates', status: 'connected' }
        ]
      },
      {
        title: 'AI Insights',
        icon: Brain,
        insights: [
          { type: 'warning', message: 'Rain forecast Saturday - expect 15% lower foot traffic' },
          { type: 'positive', message: 'Sunny weekend predicted - outdoor seating demand +30%' },
          { type: 'opportunity', message: 'Rainy day promotions increased covers by 22% historically' }
        ]
      },
      {
        title: 'Actions Available',
        icon: Target,
        actions: [
          { label: 'View Forecast', path: '/app/forecasting' },
          { label: 'Adjust Staffing', path: '/app/roster?forecast=weather' },
          { label: 'Plan Promotions', path: '/app/marketing?trigger=weather' },
          { label: 'Stock Adjustments', path: '/app/inventory?forecast=weather' }
        ]
      }
    ]
  },
  'goal-tracker': {
    title: 'Goals & OKRs',
    subtitle: 'Business objectives tracking',
    icon: Target,
    color: 'rose',
    navigateTo: '/app/goals',
    sections: [
      {
        title: 'Data Sources',
        icon: Database,
        items: [
          { name: 'Financial System', desc: 'Revenue, margins, costs vs targets', status: 'connected' },
          { name: 'Operational Metrics', desc: 'Service times, efficiency scores', status: 'connected' },
          { name: 'Customer Data', desc: 'NPS, retention, satisfaction scores', status: 'connected' },
          { name: 'HR System', desc: 'Staff satisfaction, turnover rates', status: 'connected' }
        ]
      },
      {
        title: 'AI Insights',
        icon: Brain,
        insights: [
          { type: 'positive', message: 'Q4 revenue target 92% achieved with 3 weeks remaining' },
          { type: 'warning', message: 'Staff retention goal at risk - trending 8% below target' },
          { type: 'opportunity', message: 'Increasing avg check by $5 would hit margin target' }
        ]
      },
      {
        title: 'Actions Available',
        icon: Target,
        actions: [
          { label: 'View All Goals', path: '/app/goals' },
          { label: 'Update Progress', path: '/app/goals?action=update' },
          { label: 'Set New Goals', path: '/app/goals?action=create' },
          { label: 'Team Goal Alignment', path: '/app/goals?tab=team' }
        ]
      }
    ]
  }
};

// Expandable Card Panel
export const ExpandableCardPanel = ({ cardType, onClose }) => {
  const navigate = useNavigate();
  const config = PANEL_CONFIG[cardType];
  
  if (!config) return null;
  
  const IconComponent = config.icon;
  
  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-600' },
    violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', icon: 'text-violet-600' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-600' },
    cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', icon: 'text-[#0066CC]' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: 'text-rose-600' }
  };
  
  const colors = colorClasses[config.color] || colorClasses.blue;
  
  const handleNavigate = (path) => {
    onClose?.();
    navigate(path);
  };

  return (
    <Portal><div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div 
        className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 ${colors.bg} border-b ${colors.border} z-10`}>
          <div className="p-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                <IconComponent className={`w-6 h-6 ${colors.icon}`} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{config.title}</h2>
                <p className="text-sm text-muted-foreground">{config.subtitle}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Quick Navigate Button */}
          <div className="px-4 pb-3">
            <Button 
              className="w-full bg-primary hover:bg-primary/90"
              onClick={() => handleNavigate(config.navigateTo)}
            >
              Open Full Dashboard
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {config.sections.map((section, idx) => {
            const SectionIcon = section.icon;
            
            if (section.items) {
              // Data Sources Section
              return (
                <Card key={`${section?.id ?? section?.name ?? section?.title ?? 'section'}-${idx}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <SectionIcon className="w-4 h-4 text-emerald-600" />
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {section.items.map((item, i) => (
                      <div key={`${item?.id ?? item?.name ?? item?.title ?? 'item'}-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            item.status === 'connected' ? 'bg-emerald-500' :
                            item.status === 'partial' ? 'bg-amber-500' :
                            'bg-gray-400'
                          }`} />
                          <div>
                            <p className="text-sm font-medium">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.desc}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-xs ${
                          item.status === 'connected' ? 'text-emerald-600 border-emerald-300' :
                          item.status === 'partial' ? 'text-amber-600 border-amber-300' :
                          'text-gray-500 border-gray-300'
                        }`}>
                          {item.status}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            }
            
            if (section.insights) {
              // AI Insights Section
              return (
                <Card key={`${section?.id ?? section?.name ?? section?.title ?? 'section'}-${idx}`} className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <SectionIcon className="w-4 h-4 text-violet-600" />
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {section.insights.map((insight, i) => (
                      <div 
                        key={`${insight?.id ?? insight?.name ?? insight?.title ?? 'insight'}-${i}`} 
                        className={`p-2 rounded-lg border ${
                          insight.type === 'positive' ? 'bg-emerald-50 border-emerald-200' :
                          insight.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                          'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {insight.type === 'positive' ? <TrendingUp className="w-4 h-4 text-emerald-600 mt-0.5" /> :
                           insight.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" /> :
                           <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5" />}
                          <p className="text-sm text-gray-700">{insight.message}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            }
            
            if (section.actions) {
              // Actions Section
              return (
                <Card key={`${section?.id ?? section?.title ?? 'section'}-${idx}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <SectionIcon className="w-4 h-4 text-primary" />
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {section.actions.map((action, i) => (
                      <Button 
                        key={`${action?.id ?? action?.name ?? action?.title ?? 'action'}-${i}`} 
                        variant="outline" 
                        className="w-full justify-between"
                        onClick={() => handleNavigate(action.path)}
                      >
                        {action.label}
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    ))}
                  </CardContent>
                </Card>
              );
            }
            
            return null;
          })}
        </div>
      </div>
    </div></Portal>
  );
};

export default ExpandableCardPanel;
